import React, { useEffect, useState } from 'react';
import { fetchAllReports } from '../services/reportQueries';
import SlaCard from './tiles/SlaCard';
import SimpleTable from './tiles/SimpleTable';
import ReconTrend from './tiles/ReconTrend';
import IncidentTimeline from './tiles/IncidentTimeline';

const h2 = { fontWeight: 700, fontSize: 14, marginBottom: 8 } as React.CSSProperties;
const card = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 12,
  background: '#fff',
} as React.CSSProperties;
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } as React.CSSProperties;

export default function ReportsTab({ bundle }: { bundle: any }) {
  const [domain, setDomain] = useState<string>('All');
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchAllReports(domain, bundle);
        if (!ignore) setD(data);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [domain, bundle]);

  if (!d) return <div style={{ padding: 12 }}>Loading…</div>;

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <b style={{ fontSize: 16 }}>Operations Reporting</b>
        <select
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          style={{ height: 28, border: '1px solid #d1d5db', borderRadius: 8, padding: '0 6px' }}
        >
          <option>All</option>
          <option>Sales</option>
          <option>Finance</option>
          <option>CRM</option>
          <option>Supply</option>
          <option>Inventory</option>
        </select>
        {loading && <span style={{ color: '#6b7280' }}>Loading…</span>}
      </div>

      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(200px, 1fr))', gap: 12 }}
      >
        {d.sla.map((c: any) => (
          <SlaCard key={c.id} card={c} />
        ))}
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>DQ Heatmap</div>
          <SimpleTable
            rows={d.dq}
            columns={[
              { key: 'rule', label: 'Rule', grow: true },
              { key: 'failures', label: 'Failures' },
              { key: 'blocking', label: 'Blocking' },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>Recon Drift Dashboard (14d)</div>
          <ReconTrend series={d.recon} />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>Unified Incident Timeline (sample)</div>
          <IncidentTimeline items={d.timeline} />
        </div>
        <div style={card}>
          <div style={h2}>Blast Radius Analysis</div>
          <SimpleTable
            rows={d.blast}
            columns={[
              { key: 'asset', label: 'Asset', grow: true },
              { key: 'apps', label: 'Apps' },
              { key: 'domains', label: 'Domains' },
              { key: 'sla', label: 'SLA mins' },
            ]}
          />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>End-to-End Path Trace</div>
          <SimpleTable
            rows={d.path}
            columns={[
              { key: 'from', label: 'From', grow: true },
              { key: 'to', label: 'To', grow: true },
              { key: 'edge', label: 'Edge' },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>Cluster Affinity Map</div>
          <SimpleTable
            rows={d.affinity}
            columns={[
              { key: 'cluster', label: 'Cluster', grow: true },
              { key: 'pipelines', label: '# Pipelines' },
              { key: 'notebooks', label: '# Notebooks' },
            ]}
          />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>Parallelism vs Wait Analysis</div>
          <SimpleTable
            rows={d.parallel}
            columns={[
              { key: 'pipeline', label: 'Pipeline', grow: true },
              { key: 'parallel', label: 'Parallel Tasks' },
              { key: 'waits', label: 'Wait Gates' },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>Schema Drift Watch</div>
          <SimpleTable
            rows={d.schemaDrift}
            columns={[
              { key: 'dataset', label: 'Dataset', grow: true },
              { key: 'change', label: 'Change' },
              { key: 'detected', label: 'Detected' },
            ]}
          />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>Late Arrival Forecasting</div>
          <SimpleTable
            rows={d.forecast}
            columns={[
              { key: 'dataset', label: 'Dataset', grow: true },
              { key: 'p_late', label: 'P(late 30m)' },
              { key: 'next_window', label: 'Next Window' },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>Feature Pipeline Health (MLOps)</div>
          <SimpleTable
            rows={d.featureHealth}
            columns={[
              { key: 'feature', label: 'Feature', grow: true },
              { key: 'freshness', label: 'Freshness' },
              { key: 'nulls', label: 'Null %' },
              { key: 'quality', label: 'DQ Status' },
            ]}
          />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>SLO / Error Budget</div>
          <SimpleTable
            rows={d.slo}
            columns={[
              { key: 'service', label: 'Service', grow: true },
              { key: 'slo', label: 'SLO' },
              { key: 'budget', label: 'Budget Left' },
              { key: 'period', label: 'Period' },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>MTTK Leaderboard</div>
          <SimpleTable
            rows={d.leaderboard}
            columns={[
              { key: 'team', label: 'Team', grow: true },
              { key: 'mttk', label: 'MTTK' },
            ]}
          />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>Pager Hygiene Analyzer</div>
          <SimpleTable
            rows={d.hygiene}
            columns={[
              { key: 'team', label: 'Team', grow: true },
              { key: 'noisy', label: '# Noisy Alerts' },
              { key: 'wakeups', label: 'After-hours %' },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>Hot Incident Map (Last 7d)</div>
          <SimpleTable
            rows={d.hot}
            columns={[
              { key: 'sector', label: 'Sector', grow: true },
              { key: 'sev2', label: 'SEV2' },
              { key: 'sev3', label: 'SEV3' },
            ]}
          />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>DQ Ownership Matrix</div>
          <SimpleTable
            rows={d.dqOwners}
            columns={[
              { key: 'rule', label: 'Rule', grow: true },
              { key: 'owner', label: 'Owner' },
              { key: 'contact', label: 'Contact' },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>Telemetry & Slow-query Logs</div>
          <SimpleTable
            rows={d.telemetry}
            columns={[
              { key: 'ts', label: 'Time' },
              { key: 'asset', label: 'Asset', grow: true },
              { key: 'op', label: 'Operation' },
              { key: 'ms', label: 'ms' },
            ]}
          />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>Schema / Edge Validators</div>
          <SimpleTable
            rows={d.validators}
            columns={[
              { key: 'check', label: 'Check', grow: true },
              { key: 'status', label: 'Status' },
              { key: 'details', label: 'Details', grow: true },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>RLS / ABAC Policies</div>
          <SimpleTable
            rows={d.rbac}
            columns={[
              { key: 'scope', label: 'Scope', grow: true },
              { key: 'policy', label: 'Policy' },
              { key: 'effect', label: 'Effect' },
            ]}
          />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>Capacity Forecast</div>
          <SimpleTable
            rows={d.capacity}
            columns={[
              { key: 'cluster', label: 'Cluster', grow: true },
              { key: 'p95_util', label: 'p95 Util' },
              { key: 'forecast_7d', label: 'Forecast 7d' },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>Counts Panel (Clusters by Sector)</div>
          <SimpleTable
            rows={d.counts}
            columns={[
              { key: 'sector', label: 'Sector', grow: true },
              { key: 'clusters', label: '# Clusters' },
            ]}
          />
        </div>
      </div>

      <div style={card}>
        <div style={h2}>Explain Path</div>
        <SimpleTable
          rows={d.explain}
          columns={[
            { key: 'step', label: '#' },
            { key: 'node', label: 'Node', grow: true },
            { key: 'note', label: 'Note', grow: true },
          ]}
        />
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>Error Fingerprinting</div>
          <SimpleTable
            rows={d.fingerprints}
            columns={[
              { key: 'fingerprint', label: 'Fingerprint', grow: true },
              { key: 'count', label: '# incidents' },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>Runbook Shortcuts</div>
          <SimpleTable
            rows={d.runbooks}
            columns={[
              { key: 'action', label: 'Action', grow: true },
              { key: 'endpoint', label: 'Endpoint' },
            ]}
          />
        </div>
      </div>

      <div style={grid2}>
        <div style={card}>
          <div style={h2}>Root Cause Starter (RCA auto-draft)</div>
          <SimpleTable
            rows={d.rca}
            columns={[
              { key: 'incident', label: 'Incident', grow: true },
              { key: 'summary', label: 'Summary', grow: true },
            ]}
          />
        </div>
        <div style={card}>
          <div style={h2}>Azure Tables Mode</div>
          <SimpleTable
            rows={d.sourceMode}
            columns={[
              { key: 'mode', label: 'Mode' },
              { key: 'note', label: 'Note', grow: true },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
